import {createContext, FC, ReactElement, useCallback, useContext, useMemo} from "react";
import {useOrganisation} from "../layout/OrganisationInformationProvider";
import {ActivityRenderer} from "../types";

export const LabelsContext = createContext<ActivityRenderer['id'] | undefined>(undefined);

interface Props {
    activityRenderer?: ActivityRenderer['id']
}

export const LabelsProvider: FC<Props> = ({children, activityRenderer}): ReactElement => (
    <LabelsContext.Provider value={activityRenderer}>
        {children}
    </LabelsContext.Provider>
);

type Label = 
    | 'activity'
    | 'activity_audit'
    | 'activity_pl'
    | 'student'
    | 'student_pl'
    | 'course'
    | 'course_pl'
    | 'cohort'
    | 'cohort_pl'
    | 'analytics_not_available_title'
    | 'analytics_not_available_explanation'
    | 'analytics_not_available_explanation_extra'
    | 'product_breakdown_single_renderer_title'
    | 'non_primary_attributes_name'
    | 'average_score_title'
    | 'average_time_spent_title'
    | 'average_time_spent_hint'
    | 'unnecessary_orders_title'
    | 'missed_items_title'
    | 'summary_title'
    | 'commonly_missed_title'
    | 'average_missed_items_title'
    | 'section_performance_title'
    | 'average_section_performance_title'
    | 'answer_optimality_title'
    | 'pass_threshold'
    | 'pass_threshold_tooltip'
    | 'pass_threshold_zero'
    | 'pass_threshold_zero_tooltip'


export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export type Translate = (label: Label, capitalizeFirstLetter?: any) => string;

export const useLabels = () => {
    const organisation = useOrganisation();
    const allActivityRenderers = useMemo(() => organisation.products?.reduce(
            (renderers, product) => renderers.concat(product.activity_renderers), [] as ActivityRenderer[])
        , [organisation]);
    const rendererId = useContext(LabelsContext);
    const activityRenderer = useMemo<ActivityRenderer | undefined>(() =>
            allActivityRenderers?.find(activityRenderer => activityRenderer.id === rendererId),
        [allActivityRenderers, rendererId]);

    const t = useCallback<Translate>((label, capitalizeFirstLetter = true) => {
        let text = organisation?.labels?.[label];
        if (activityRenderer && activityRenderer.theme_label_overrides[label]) {
            text = activityRenderer.theme_label_overrides[label];
        }
        text = text ?? label;
        return capitalizeFirstLetter ? capitalize(text) : text;
    }, [organisation, activityRenderer]);
    return {t};
}